import React from 'react';
import Icon from '../../../components/AppIcon';

const TimelineView = ({ milestones, currentTime }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success border-success';
      case 'in-progress':
        return 'bg-primary border-primary';
      case 'delayed':
        return 'bg-error border-error';
      case 'upcoming':
        return 'bg-muted border-border';
      default:
        return 'bg-muted border-border';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return 'CheckCircle2';
      case 'in-progress':
        return 'Clock';
      case 'delayed':
        return 'AlertCircle';
      case 'upcoming':
        return 'Circle';
      default:
        return 'Circle';
    }
  };

  const formatTime = (date) => {
    return new Date(date)?.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="bg-card rounded-xl border-2 border-border p-4 md:p-5 lg:p-6 h-full overflow-hidden flex flex-col">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-2 md:gap-3">
          <Icon name="Calendar" size={24} color="var(--color-primary)" />
          <h2 className="text-lg md:text-xl font-semibold text-foreground">
            Production Timeline
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
          <span className="caption text-muted-foreground whitespace-nowrap">
            Live: {formatTime(currentTime)}
          </span>
        </div>
      </div>
      <div className="flex-1 overflow-x-auto overflow-y-hidden">
        <div className="relative min-w-max pb-4">
          <div className="absolute top-8 left-0 right-0 h-0.5 bg-border" />

          <div className="flex items-start gap-4 md:gap-6 lg:gap-8 relative">
            {milestones?.map((milestone, index) => (
              <div key={milestone?.id} className="flex flex-col items-center min-w-[120px] md:min-w-[160px]">
                <div className={`w-12 h-12 md:w-16 md:h-16 rounded-full border-4 ${getStatusColor(milestone?.status)} flex items-center justify-center z-10 bg-card`}>
                  <Icon
                    name={getStatusIcon(milestone?.status)}
                    size={20}
                    color={milestone?.status === 'completed' ? 'var(--color-success)' : milestone?.status === 'in-progress' ? 'var(--color-primary)' : milestone?.status === 'delayed' ? 'var(--color-error)' : 'var(--color-muted-foreground)'}
                  />
                </div>

                <div className="mt-4 text-center">
                  <p className="text-xs md:text-sm font-semibold text-foreground line-clamp-2">
                    {milestone?.title}
                  </p>
                  <p className="caption text-muted-foreground mt-1 whitespace-nowrap">
                    {formatDate(milestone?.scheduledTime)}
                  </p>
                  <p className="caption text-muted-foreground whitespace-nowrap">
                    {formatTime(milestone?.scheduledTime)}
                  </p>

                  {milestone?.status === 'in-progress' && (
                    <div className="mt-2 px-2 py-1 bg-primary/10 rounded-md">
                      <p className="caption text-primary font-medium data-text">
                        {milestone?.progress}% Complete
                      </p>
                    </div>
                  )}

                  {milestone?.status === 'delayed' && (
                    <div className="mt-2 px-2 py-1 bg-error/10 rounded-md">
                      <p className="caption text-error font-medium">
                        {milestone?.delayMinutes}m behind
                      </p>
                    </div>
                  )}

                  {milestone?.status === 'completed' && (
                    <div className="mt-2 px-2 py-1 bg-success/10 rounded-md">
                      <p className="caption text-success font-medium">
                        Completed
                      </p>
                    </div>
                  )}
                </div>

                <div className="mt-3 space-y-1">
                  <div className="flex items-center gap-1.5 justify-center">
                    <Icon name="Package" size={12} color="var(--color-muted-foreground)" />
                    <span className="caption text-muted-foreground data-text">
                      {milestone?.unitsProduced}/{milestone?.targetUnits}
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 justify-center">
                    <Icon name="Users" size={12} color="var(--color-muted-foreground)" />
                    <span className="caption text-muted-foreground data-text">
                      {milestone?.workersAssigned}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 mt-4 md:mt-6 pt-4 border-t border-border">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-success border-2 border-success" />
          <span className="caption text-muted-foreground">Completed</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-primary border-2 border-primary" />
          <span className="caption text-muted-foreground">In Progress</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-error border-2 border-error" />
          <span className="caption text-muted-foreground">Delayed</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-muted border-2 border-border" />
          <span className="caption text-muted-foreground">Upcoming</span>
        </div>
      </div>
    </div>
  );
};

export default TimelineView;